import { TherapyTarget } from 'domains/signup/data/types';
import { supportedStates } from 'utils/states';
import { MachineStateType } from 'utils/xstate/types';
import { assign, setup } from 'xstate';

// note(juan): this is part of the context and its expected to come from outside, set by whomever executes the machine
export type ContactInfoMachineInput = {
  offerLivePhoneCall: boolean;
};

const DEFAULT_INPUT: ContactInfoMachineInput = {
  offerLivePhoneCall: false,
};

export type CompletedContactInfoMachineContext = {
  target: TherapyTarget;

  state: string;

  firstName: string;
  lastName: string;

  phoneNumber: string;

  email: string;
  receiveNewsletter: boolean;
};

export type ContactInfoResult = CompletedContactInfoMachineContext;

type InternalContext = Partial<CompletedContactInfoMachineContext> & {
  machineConfig: ContactInfoMachineInput;
};

/** @xstate-layout N4IgpgJg5mDOIC5QGMD2A7ALgQ2ZgkugGaoCyuAFgJbpgB0YWYATgHLYC2YAxLAK4AjDlUzsuAbQAMAXUSgADqlgiqGOSAAeiALQAmAKx0A7AA4AnJIBsBgDQgAnogDMluvt1OALE6P6Avn52aFi4BMRklDT0jJgsAAoUGGCsfBwCLNwCuADWUrJIIIrKmKro6loIepYmxpJOAIy2Doj6ZnQGAUEYOHiEJOTI1LQMTMwJSSlpGfxCIuO0k+nMeepFKmoFFdr6rkaeuvUmvnaOCO71nSDBPWH9kcMxLACiHNhUADa8gsKYL2-vKwKaxKG1AW10nna+lMjX0J0QRyMl2uoT6EUGURGsWYfw+mRygIUSnWZU2Ol01VqDSap303mMFJ2HUCV26qPCAyG0VGAGUcLEvrNMHzsLFCYViSDSWCdPVJLp2p4zMqLPU1foTPUjPDKkYjJI6PKNUrvPLJI1kWzehz7tzsSKBTMfg6wOJ6vkicVSuVEBDDGqTCZ3J56pYvPUnCYddo9kZ2srdGZ9GqjE4zLpLSFrXcMcNUEQiCwADJUABuYHmYAAwth3p9YNkqPJKzW6+Lgd6yZUGm401YlZY1crzdHIwrTGanPLTGY1ZYAiz0KgIHB1Cjs+iuatJZ2Zd3Q1TYdG5ZJIfpz55JJJk5ZPIGzJmbmjOZjHmxOGBt17QZpZVHmpUoYmI+7I5lyWLxIkCypEsX4kj6lR3nQZiWJIZg+HCAFTga-gsuutybq+oy4u8cFSgh2gHp41THABeqeCBG4vsMRA0FQsAUJAZG7r+gEGgcaEYTqcr1G0sKMQRzF2iwLrcT+WwHhCByarekbeJYGrRrOcbXvoaZasmJh1BJz62nQsD8skqDCnw8iKMwsQQHJ0q8XSZ5OD4RhmKYHn6ro0axvG6ZJimaYZnhVqSWZ+aFswJbli2takUCO7yTo1F0CYuh1LompOIZljVJhpzbO4bioVeTi6KmuXWAufhAA */
export const contactInfoMachine = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitName'; firstName: string; lastName: string }
      | { type: 'submitEmail'; email: string; receiveNewsletter: boolean }
      | { type: 'submitPhoneNumber'; phoneNumber: string }
      | { type: 'submitState'; state: string }
      | { type: 'skipPhoneCall' },

    input: {} as ContactInfoMachineInput,
    output: {} as ContactInfoResult,
  },

  actions: {
    saveState: assign((_, params: { state: string }) => ({
      state: params.state,
    })),
    saveName: assign((_, params: { firstName: string; lastName: string }) => ({
      firstName: params.firstName,
      lastName: params.lastName,
    })),
    saveEmail: assign((_, params: { email: string; receiveNewsletter: boolean }) => ({
      email: params.email,
      receiveNewsletter: params.receiveNewsletter,
    })),
    savePhoneNumber: assign((_, params: { phoneNumber: string }) => ({
      phoneNumber: params.phoneNumber,
    })),
  },
  guards: {
    isStateSupported: (_, params: { state: string }) => supportedStates.includes(params.state),
  },
}).createMachine({
  id: 'contactInfoMachine',
  context: ({ input }) => ({ machineConfig: input ?? DEFAULT_INPUT }),
  initial: 'enterState',
  output: ({ context }) => context as ContactInfoResult,
  states: {
    enterName: {
      on: {
        submitName: {
          target: 'enterPhoneNumber',
          actions: {
            type: 'saveName',
            params: ({ event }) => event,
          },
        },
      },
    },

    enterPhoneNumber: {
      on: {
        back: 'enterName',

        submitPhoneNumber: {
          target: 'enterEmail',
          actions: {
            type: 'savePhoneNumber',
            params: ({ event }) => event,
          },
        },
      },
    },

    enterEmail: {
      on: {
        submitEmail: {
          target: 'finished',
          actions: {
            type: 'saveEmail',
            params: ({ event }) => event,
          },
        },
        back: 'enterPhoneNumber',
      },
    },

    finished: {
      type: 'final',
    },

    enterState: {
      on: {
        submitState: [
          {
            target: 'offerLivePhoneCall',
            guard: {
              type: 'isStateSupported',
              params: ({ event }) => event,
            },
            actions: {
              type: 'saveState',
              params: ({ event }) => event,
            },
          },
          'stateNotSupported',
        ],
      },
    },

    stateNotSupported: {},
    offerLivePhoneCall: {
      on: {
        skipPhoneCall: 'enterName',
      },
    },
  },
});

export type ContactInfoState = MachineStateType<typeof contactInfoMachine>;
